import React, { useState, useEffect } from 'react';
import { ShieldCheck, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { farmvestService } from '../../services/farmvest_api';


/* -------------------- Modal (Success/Error) -------------------- */
const Modal = ({
    isOpen,
    type,
    message,
    onClose,
}: {
    isOpen: boolean;
    type: 'success' | 'error';
    message: string;
    onClose: () => void;
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
                <h3
                    className={`text-lg font-bold text-center mb-2 ${type === 'success' ? 'text-gray-900' : 'text-red-900'
                        }`}
                >
                    {type === 'success' ? 'Success!' : 'Error'}
                </h3>

                <p className="text-sm text-center text-gray-500 mb-6">{message}</p>

                <button
                    onClick={onClose}
                    className={`w-full py-2.5 rounded-xl font-semibold text-white shadow-sm transition-all active:scale-95 ${type === 'success'
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-green-200'
                        : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow-red-200'
                        }`}
                >
                    OK
                </button>
            </div>
        </div>
    );
};

/* -------------------- Confirm Modal (OK/Cancel) -------------------- */
const ConfirmModal = ({
    isOpen,
    title,
    message,
    okText = 'OK',
    cancelText = 'Cancel',
    onConfirm,
    onCancel,
    type = 'info',
}: {
    isOpen: boolean;
    title: string;
    message: string;
    okText?: string;
    cancelText?: string;
    type?: 'info' | 'danger';
    onConfirm: () => void;
    onCancel: () => void;
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
                <h3 className="text-lg font-bold text-center text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-center text-gray-600 mb-6">{message}</p>

                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 py-2.5 rounded-xl font-semibold bg-gray-100 hover:bg-gray-200 text-gray-900 transition-all active:scale-95"
                    >
                        {cancelText}
                    </button>

                    <button
                        type="button"
                        onClick={onConfirm}
                        className={`flex-1 py-2.5 rounded-xl font-semibold text-white shadow-sm transition-all active:scale-95 ${type === 'danger'
                            ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow-red-200'
                            : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-200'
                            }`}
                    >
                        {okText}
                    </button>
                </div>
            </div>
        </div>
    );
};

type Mode = 'activate' | 'deactivate';

const FarmvestUserActivationPage = () => {
    // Mock local state instead of Redux
    const [loading, setLoading] = useState(false);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [availableRoles, setAvailableRoles] = useState<string[]>([]);
    const [isAutoFilled, setIsAutoFilled] = useState(false);
    const [statusWarning, setStatusWarning] = useState('');

    const [mode, setMode] = useState<Mode>('deactivate');

    const [formData, setFormData] = useState({
        mobile: '',
        first_name: '',
        last_name: '',
        email: '',
        remarks: '',
        role: '',
    });

    const [confirmConfig, setConfirmConfig] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        type: 'info' | 'danger';
        onConfirm: null | (() => void);
    }>({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        onConfirm: null,
    });

    const [modalConfig, setModalConfig] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        message: string;
    }>({
        isOpen: false,
        type: 'success',
        message: '',
    });



    const resetLocalForm = () => {
        setFormData({ mobile: '', first_name: '', last_name: '', email: '', remarks: '', role: '' });
        setAvailableRoles([]);
        setStatusWarning('');
    };

    const handleModeSwitch = (newMode: Mode) => {
        setMode(newMode);
        resetLocalForm();
    };

    useEffect(() => {
        if (formData.mobile.length !== 10) {
            setAvailableRoles([]);
            setIsAutoFilled(false);
            setStatusWarning('');
            setFormData(prev => ({ ...prev, first_name: '', last_name: '', email: '', role: '' }));
            return;
        }
        const fetchRoles = async () => {
            setRolesLoading(true);
            try {
                const response = await farmvestService.getUserData(formData.mobile);
                const user = response?.user || response;

                const roles: string[] = [];
                if (user) {
                    let userRoles: string[] = [];
                    if (Array.isArray(user.roles_detail)) {
                        userRoles = user.roles_detail
                            .filter((rd: { role: string; is_active: boolean }) =>
                                mode === 'deactivate' ? rd.is_active : !rd.is_active
                            )
                            .map((rd: { role: string; is_active: boolean }) => rd.role);

                        if (userRoles.length === 0 && user.roles_detail.length > 0) {
                            setStatusWarning(
                                mode === 'activate'
                                    ? 'This user is already active.'
                                    : 'This user is already inactive.'
                            );
                        } else {
                            setStatusWarning('');
                        }
                    } else if (Array.isArray(user.roles)) {
                        userRoles = user.roles;
                    } else if (typeof user.roles === 'string') {
                        userRoles = user.roles.split(',').map((r: string) => r.trim());
                    } else if (user.role) {
                        userRoles = [user.role];
                    }
                    userRoles.forEach(r => {
                        const normalized = String(r).trim().toUpperCase();
                        if (normalized && !roles.includes(normalized)) roles.push(normalized);
                    });

                    setFormData(prev => ({
                        ...prev,
                        first_name: user.first_name?.trim() || prev.first_name,
                        last_name: user.last_name?.trim() || prev.last_name,
                        email: user.email?.trim() || prev.email,
                        role: roles.length === 1 ? roles[0] : prev.role,
                    }));
                    setIsAutoFilled(true);
                }

                setAvailableRoles(roles);
            } catch {
                setAvailableRoles([]);
            } finally {
                setRolesLoading(false);
            }
        };
        fetchRoles();
    }, [formData.mobile, mode]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        if (name === 'first_name' || name === 'last_name') {
            // Only allow letters and spaces
            if (/[0-9]/.test(value)) return;
        }

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    /* -------------------- CONFIRM POPUP BEFORE OTP REQUEST -------------------- */
    const handleMobileSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // ✅ Mobile Validation
        if (!/^\d{10}$/.test(formData.mobile)) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                message: 'Please enter a valid 10-digit mobile number.',
            });
            return;
        }

        // ✅ Role Validation
        if (!formData.role) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                message: 'Please select a role.',
            });
            return;
        }

        // ✅ Name Validation
        if (!formData.first_name) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                message: 'First name is required.',
            });
            return;
        }

        if (formData.first_name.length > 30 || formData.last_name.length > 30) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                message: 'First name and Last name must be within 30 characters.',
            });
            return;
        }

        // ✅ Email Validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                message: 'Please enter a valid email address.',
            });
            return;
        }

        // ✅ Remarks Validation
        if (!formData.remarks || formData.remarks.trim().length < 5) {
            setModalConfig({
                isOpen: true,
                type: 'error',
                message: 'Please provide a valid reason in the remarks (min 5 characters).',
            });
            return;
        }

        // ✅ Direct OTP request without status pre-check as per latest API requirements
        // ✅ Direct activation/deactivation after confirmation
        setConfirmConfig({
            isOpen: true,
            title: mode === 'deactivate' ? 'Confirm Deactivation' : 'Confirm Activation',
            message:
                mode === 'deactivate'
                    ? `Are you sure you want to deactivate the account for +91 ${formData.mobile}?`
                    : `Are you sure you want to activate the account for +91 ${formData.mobile}?`,
            type: mode === 'deactivate' ? 'danger' : 'info',
            onConfirm: async () => {
                setConfirmConfig((prev) => ({ ...prev, isOpen: false }));
                setLoading(true);

                try {
                    const finalRemarks = `${formData.remarks.trim()} | By: ${formData.first_name} ${formData.last_name}`;
                    if (mode === 'deactivate') {
                        await farmvestService.deactivateUser(formData.mobile, finalRemarks, formData.role || undefined);
                    } else {
                        await farmvestService.activateUser(formData.mobile, finalRemarks, formData.role || undefined);
                    }

                    setLoading(false);
                    setModalConfig({
                        isOpen: true,
                        type: 'success',
                        message: `Account ${mode === 'deactivate' ? 'deactivated' : 'activated'} successfully`,
                    });
                } catch (error: any) {
                    setLoading(false);
                    setModalConfig({
                        isOpen: true,
                        type: 'error',
                        message: error.response?.data?.detail || error.response?.data?.message || 'Failed to process request. Please try again.',
                    });
                }
            },
        });
    };

    const handleCloseModal = () => {
        setModalConfig({ ...modalConfig, isOpen: false });

        if (modalConfig.type === 'success') {
            resetLocalForm();
        }
    };

    return (
        <div className="min-h-full flex flex-col flex-1 font-['Outfit','Inter',sans-serif] text-slate-800 bg-gradient-to-br from-[#f0fdf4] to-[#dcfce7] relative scrollbar-hide">
            {/* Header & Sub-header Container */}
            <header className="sticky top-0 z-[1000] shadow-md w-full">
                {/* Navbar */}
                <nav className="flex justify-between items-center px-[5%] py-5 bg-white/80 backdrop-blur-xl border-b border-white/30">
                    <div className="flex items-center gap-3">
                        <span className="bg-gradient-to-br from-green-700 to-green-600 bg-clip-text text-transparent font-extrabold -tracking-tight text-[1.4rem] sm:text-[1.75rem]">
                            Farmvest Global Pvt Ltd
                        </span>
                    </div>
                </nav>

                <div className="bg-gradient-to-r from-green-600 to-green-700 text-white text-center p-2.5 text-[0.9rem] font-medium tracking-wide shadow-xl shadow-green-600/15">
                    {mode === 'deactivate' ? 'Deactivate your account securely' : 'Activate your account securely'}
                </div>
            </header>
            <div className="overflow-x-hidden w-full relative z-[1] before:content-[''] before:absolute before:-top-[10%] before:-right-[5%] before:w-[600px] before:h-[600px] before:bg-[radial-gradient(circle,rgba(22,163,74,0.1)_0%,rgba(255,255,255,0)_70%)] before:rounded-full before:z-0">

                <section className="flex flex-wrap lg:flex-nowrap max-w-[1400px] mx-auto my-5 px-6 sm:px-10 gap-10 lg:gap-[60px] items-stretch justify-between relative z-[1]">
                    {/* Left */}
                    <div className="flex-1 min-w-[300px] lg:max-w-[550px] flex flex-col">
                        <div className="relative w-full h-full flex items-center justify-center bg-white/60 backdrop-blur-md border border-white/40 rounded-[32px] shadow-2xl shadow-black/5 p-0 overflow-hidden transition-transform duration-500 hover:-translate-y-2">
                            <img src="/farmvest family.png" alt="Farmvest Family" className="w-full h-full object-cover block" />
                        </div>
                    </div>

                    {/* Right */}
                    <div className="flex-initial lg:basis-[450px] w-full max-w-full flex flex-col">
                        <div className="bg-white p-5 sm:p-6 rounded-[24px] sm:rounded-[32px] shadow-2xl shadow-black/10 border border-black/2 relative overflow-hidden flex flex-col justify-center h-full">
                            {/* Mode Switch */}
                            <div className="flex gap-1.5 mb-3 p-1 rounded-[16px] bg-slate-50 border border-slate-200">
                                <button
                                    type="button"
                                    className={`flex-1 font-bold text-[0.85rem] cursor-pointer transition-all duration-300 py-1.5 px-2 rounded-[12px] ${mode === 'activate' ? 'bg-white text-green-600 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'}`}
                                    onClick={() => handleModeSwitch('activate')}
                                >
                                    Activate
                                </button>

                                <button
                                    type="button"
                                    className={`flex-1 font-bold text-[0.85rem] cursor-pointer transition-all duration-300 py-1.5 px-2 rounded-[12px] ${mode === 'deactivate' ? 'bg-white text-red-500 shadow-sm' : 'bg-transparent text-slate-500 hover:text-slate-700'}`}
                                    onClick={() => handleModeSwitch('deactivate')}
                                >
                                    Deactivate
                                </button>
                            </div>

                            <div className="text-center mb-3">
                                <h2 className="text-slate-900 text-[1.5rem] font-extrabold mb-1 -tracking-tight">
                                    {mode === 'deactivate' ? 'Deactivate Account' : 'Activate Account'}
                                </h2>

                                <p className="text-slate-500 text-[0.85rem] leading-relaxed mb-1.5">
                                    {mode === 'deactivate' ? (
                                        <span className="font-semibold text-black">We're sorry to see you go.</span>
                                    ) : (
                                        <span className="font-semibold text-black">Welcome back! Let's activate your account.</span>
                                    )}
                                </p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleMobileSubmit}>
                                {/* Mobile */}
                                <div className="relative mb-4">
                                    <div className="absolute top-0 left-0 h-full w-14 flex items-center justify-center pointer-events-none z-10">
                                        <span className="text-gray-500 font-medium text-base pt-0.5">+91</span>
                                    </div>

                                    <input
                                        type="tel"
                                        name="mobile"
                                        placeholder="Enter registered mobile *"
                                        className="w-full pl-14 pr-5 py-3 border-2 border-transparent rounded-[16px] text-base font-medium outline-none transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] bg-gray-100 text-gray-800 focus:bg-white focus:border-green-600 focus:ring-4 focus:ring-green-600/10 placeholder-gray-400"
                                        value={formData.mobile}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setFormData({ ...formData, mobile: val });
                                        }}
                                        required
                                    />
                                </div>

                                {statusWarning && (
                                    <p className="-mt-2 mb-3 text-xs font-semibold text-amber-600 flex items-center gap-1">
                                        <span className="inline-flex w-4 h-4 rounded-full bg-amber-100 items-center justify-center text-amber-600 shrink-0">!</span>
                                        {statusWarning}
                                    </p>
                                )}

                                {/* Role Dropdown */}
                                <div className="relative mb-4">
                                    <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                                        {rolesLoading
                                            ? <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                            : <ChevronDown size={16} className="text-gray-400" />
                                        }
                                    </div>
                                    <select
                                        value={formData.role}
                                        onChange={e => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                        disabled={rolesLoading || availableRoles.length === 0 || availableRoles.length === 1}
                                        className="w-full appearance-none px-5 py-3 pr-10 border-2 border-transparent rounded-[16px] text-base font-medium outline-none transition-all duration-200 bg-gray-100 text-gray-800 focus:bg-white focus:border-green-600 focus:ring-4 focus:ring-green-600/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value="">
                                            {rolesLoading
                                                ? 'Fetching roles...'
                                                : availableRoles.length === 0 && formData.mobile.length === 10
                                                    ? 'No roles found'
                                                    : 'Select Role *'}
                                        </option>
                                        {availableRoles.map(r => (
                                            <option key={r} value={r}>{r}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* First + Last */}
                                <div className="grid grid-cols-2 gap-2.5 mb-4">
                                    <input
                                        type="text"
                                        name="first_name"
                                        placeholder="First Name *"
                                        className="px-5 py-1.5 border-2 border-transparent rounded-[12px] text-base font-medium outline-none transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] bg-gray-100 text-gray-800 focus:bg-white focus:border-green-600 focus:ring-4 focus:ring-green-600/10 placeholder-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        maxLength={30}
                                        disabled={isAutoFilled}
                                        required
                                    />
                                    <input
                                        type="text"
                                        name="last_name"
                                        placeholder="Last Name (Optional)"
                                        className="px-5 py-1.5 border-2 border-transparent rounded-[12px] text-base font-medium outline-none transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] bg-gray-100 text-gray-800 focus:bg-white focus:border-green-600 focus:ring-4 focus:ring-green-600/10 placeholder-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        maxLength={30}
                                        disabled={isAutoFilled}
                                    />
                                </div>

                                {/* Email */}
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email Address (Optional)"
                                    className="w-full px-5 py-4 mb-4 border-2 border-transparent rounded-[16px] text-base font-medium outline-none transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] bg-gray-100 text-gray-800 focus:bg-white focus:border-green-600 focus:ring-4 focus:ring-green-600/10 placeholder-gray-400 disabled:opacity-60 disabled:cursor-not-allowed"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={isAutoFilled}
                                />

                                {/* Remarks */}
                                <textarea
                                    name="remarks"
                                    placeholder="Enter Remarks *"
                                    className="w-full px-5 py-3 mb-4 border-2 border-transparent rounded-[16px] text-base font-medium outline-none transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] bg-gray-100 text-gray-800 focus:bg-white focus:border-green-600 focus:ring-4 focus:ring-green-600/10 placeholder-gray-400 h-[60px] pt-3 resize-none"
                                    value={formData.remarks}
                                    onChange={handleInputChange}
                                    required
                                />

                                <button
                                    type="submit"
                                    disabled={loading || !formData.mobile || !!statusWarning}
                                    className={`w-full font-bold py-2.5 rounded-[16px] transition-all duration-300 mt-3 shadow-lg uppercase tracking-widest text-[0.9rem] hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed text-white ${loading || !formData.mobile
                                        ? 'bg-slate-400'
                                        : !!statusWarning
                                            ? 'bg-slate-400'
                                            : mode === 'deactivate'
                                                ? 'bg-red-500 hover:bg-red-600 shadow-red-500/30'
                                                : 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/30'
                                        }`}
                                >
                                    {loading ? 'Processing...' : 'Submit'}
                                </button>
                            </form>

                            <div className="text-center text-xs text-black mt-2">
                                <Link to="/privacy-policy" className="underline hover:text-blue-600 border-none bg-transparent p-0 m-0" target="_blank">
                                    Terms and Policy
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>



                {/* ✅ Confirm modal */}
                <ConfirmModal
                    isOpen={confirmConfig.isOpen}
                    title={confirmConfig.title}
                    message={confirmConfig.message}
                    type={confirmConfig.type}
                    okText="OK"
                    cancelText="Cancel"
                    onConfirm={() => confirmConfig.onConfirm?.()}
                    onCancel={() => setConfirmConfig((prev) => ({ ...prev, isOpen: false }))}
                />

                {/* Success/Error modal */}
                <Modal isOpen={modalConfig.isOpen} type={modalConfig.type} message={modalConfig.message} onClose={handleCloseModal} />
            </div>
        </div>
    );
};

export default FarmvestUserActivationPage;
